import { notifyOpusWebCodecsUnsupportedOnce } from './opusHead';
import { OpusWasmDownstreamSession } from './opusWasmDownstreamDecoder';
import { OpusWebCodecsDownstreamSession } from './opusWebCodecsDownstreamDecoder';

interface OpusDownstreamDecoderBackend {
  ensureConfigured(): Promise<boolean>;
  decodePacket(packet: Uint8Array): Promise<Float32Array>;
  close(): void;
}

/**
 * 每个设备一条独立解码会话：优先 WebCodecs，配置失败后懒加载 WASM libopus。
 */
export class OpusDownstreamSession {
  private backend: OpusDownstreamDecoderBackend | null = null;
  private configuring: Promise<boolean> | null = null;
  private closed = false;

  constructor(private readonly deviceId: string) {}

  getDeviceId(): string {
    return this.deviceId;
  }

  async ensureConfigured(): Promise<boolean> {
    if (this.closed) {
      return false;
    }
    if (this.backend) {
      return true;
    }
    if (this.configuring) {
      return this.configuring;
    }

    this.configuring = this.configureBackend();
    try {
      return await this.configuring;
    } finally {
      this.configuring = null;
    }
  }

  private async configureBackend(): Promise<boolean> {
    const webCodecs = new OpusWebCodecsDownstreamSession(this.deviceId);
    if (await webCodecs.ensureConfigured()) {
      if (this.closed) {
        webCodecs.close();
        return false;
      }
      this.backend = webCodecs;
      return true;
    }
    webCodecs.close();

    const wasm = new OpusWasmDownstreamSession(this.deviceId);
    if (await wasm.ensureConfigured()) {
      if (this.closed) {
        wasm.close();
        return false;
      }
      this.backend = wasm;
      return true;
    }
    wasm.close();

    notifyOpusWebCodecsUnsupportedOnce();
    return false;
  }

  async decodePacket(packet: Uint8Array): Promise<Float32Array> {
    if (!this.backend) {
      throw new Error('Opus decoder not ready');
    }
    return this.backend.decodePacket(packet);
  }

  close(): void {
    this.closed = true;
    this.backend?.close();
    this.backend = null;
  }
}
